import { OperatorEventSummaryI } from './operator';
import { extractWallClockTime } from './ct-time.util';
import { formatCentralDate } from './time-format.util';

/**
 * Display helpers for an operator's own event rows (#2399).
 *
 * Shared between the operator start screen and the profile schedule, so they
 * live here rather than in either consumer — per the one-library rule for
 * anything that crosses a lib boundary.
 */

/** "Friday, July 25" — the CT calendar day the event runs on. */
export function formatOperatorEventDay(event: Pick<OperatorEventSummaryI, 'testEventDate'>): string {
  return formatCentralDate(event.testEventDate, 'EEEE, MMMM d');
}

/**
 * "9:00 AM – 1:00 PM" from the stored start/end strings.
 *
 * `TestingEvent.startTime` is a plain string column with three real shapes in
 * live data (bare "HH:mm", CT-offset ISO, legacy "9:00 PM"), so the wall clock
 * is salvaged with the shared lenient parser rather than assumed. Falls back to
 * the raw stored values when neither side parses — showing something the
 * operator can reconcile against beats showing nothing.
 */
export function formatOperatorEventTimeRange(event: Pick<OperatorEventSummaryI, 'startTime' | 'endTime'>): string {
  const start = formatWallClock(event.startTime);
  const end = formatWallClock(event.endTime);
  if (!start && !end) {
    return `${event.startTime} – ${event.endTime}`;
  }
  return `${start ?? event.startTime} – ${end ?? event.endTime}`;
}

/** "14:30" → "2:30 PM". Null when the value holds no time-shaped reading. */
function formatWallClock(value: string): string | null {
  const hhmm = extractWallClockTime(value);
  if (!hhmm) {
    return null;
  }
  const [hourPart, minutePart] = hhmm.split(':');
  const hour24 = parseInt(hourPart, 10);
  const meridiem = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minutePart} ${meridiem}`;
}
